import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Users, Search } from 'lucide-react'
import type { FpdPost, Scope } from '../../core/types'
import { useAuth } from '../../core/auth'
import { SCOPE_LABELS } from '../../core/caps'
import { Loading, Empty, ErrorState } from '../../ui/States'
import { Text, Area } from '../../ui/Field'
import { useFpdConfig, useFpdDays, usePosts, createPost, joinPost, leavePost, setPostOpen, deletePost, buildingsFor, type PostInput } from './model'
import { schoolYearOf, daysFor, upcoming, isoToday, toMin, fromMin, clock } from './logic'

/**
 * Finding collaborators: a teacher posts a date and what they want to work
 * on, and anyone in the district with Flex PD sees it and can join. Joining
 * is not approval: each member starts their own request from the post, with
 * the others named, and their own principal decides it.
 */

const fmt = (iso: string) => new Date(iso + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })

export function Collaborate() {
  const { profile, user } = useAuth()
  const me = { email: (user?.email ?? '').toLowerCase(), name: profile?.displayName ?? '' }
  const sy = schoolYearOf()
  const posts = usePosts(sy.id, true)
  const myBuildings = buildingsFor(profile?.caps, profile?.districtAdmin, 'flexpd.request')
  const [posting, setPosting] = useState(false)
  const [focus, setFocus] = useState('')
  const [building, setBuilding] = useState<Scope | ''>('')
  const [q, setQ] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const cfg = useFpdConfig()
  if (posts === null || !cfg) return <Loading label="Loading collaborations" />

  const today = isoToday()
  const live = posts.filter((p) => p.date >= today).sort((a, b) => a.date.localeCompare(b.date))
  const mine = live.filter((p) => p.organizerEmail === me.email || p.memberEmails.includes(me.email))
  const others = live.filter((p) => p.open && !mine.includes(p))
    .filter((p) => !focus || p.focus === focus)
    .filter((p) => !building || p.organizerBuilding === building || p.members.some((m) => m.building === building))
    .filter((p) => !q || `${p.idea} ${p.organizerName} ${p.focus}`.toLowerCase().includes(q.toLowerCase()))
  const focuses = [...new Set(live.map((p) => p.focus))].sort()
  const run = (fn: () => Promise<unknown>) => async () => {
    setErr(null)
    try { await fn() } catch (e) { setErr(e instanceof Error ? e.message : String(e)) }
  }

  return (
    <>
      <Link to="/flex-pd" className="backlink">← Flex PD</Link>
      <div className="fpd__head" style={{ marginTop: 0 }}>
        <div>
          <h1 className="page-title">Find collaborators</h1>
          <p className="page-sub">Teachers anywhere in the district, looking for others to do flex PD with.</p>
        </div>
        {myBuildings.length > 0 && !posting && <button className="btn" style={{ width: 'auto' }} onClick={() => setPosting(true)}>+ Post a collaboration</button>}
      </div>
      {err && <ErrorState body={`Not saved. ${err}`} />}
      {posting && <PostForm onDone={() => setPosting(false)} myBuildings={myBuildings} me={me} />}

      {mine.length > 0 && (
        <>
          <h2 className="work__heading">Yours</h2>
          <ul className="fpd__posts">{mine.map((p) => <PostCard key={p.id} p={p} me={me} myBuilding={myBuildings[0]} run={run} />)}</ul>
        </>
      )}

      <h2 className="work__heading">Looking for collaborators</h2>
      <div className="fpd__filters" style={{ marginTop: 0 }}>
        <label className="sel__search"><Search size={15} aria-hidden="true" />
          <input className="opt__input" placeholder="Search ideas or names" aria-label="Search collaborations" value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
        <select className="opt__input" style={{ maxWidth: 240 }} value={focus} aria-label="Learning focus" onChange={(e) => setFocus(e.target.value)}>
          <option value="">Every focus</option>
          {focuses.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <select className="opt__input" style={{ maxWidth: 200 }} value={building} aria-label="Building" onChange={(e) => setBuilding(e.target.value as Scope | '')}>
          <option value="">Every building</option>
          {(['hs', 'ms', 'es'] as Scope[]).map((b) => <option key={b} value={b}>{SCOPE_LABELS[b]}</option>)}
        </select>
      </div>
      {others.length === 0 ? (
        <Empty title={focus || building || q ? 'Nothing matches' : mine.length ? 'Nobody else is looking right now' : 'Nobody is looking yet'}
          body={myBuildings.length ? 'Post what you want to work on, and teachers across the district can join you.' : 'Teachers post collaborations here.'} />
      ) : (
        <ul className="fpd__posts">{others.map((p) => <PostCard key={p.id} p={p} me={me} myBuilding={myBuildings[0]} run={run} />)}</ul>
      )}
    </>
  )
}

function PostCard({ p, me, myBuilding, run }: {
  p: FpdPost; me: { email: string; name: string }; myBuilding: Scope | undefined
  run: (fn: () => Promise<unknown>) => () => Promise<void>
}) {
  const organizer = p.organizerEmail === me.email
  const member = p.memberEmails.includes(me.email)
  const full = p.spots != null && p.memberEmails.length >= p.spots
  const people = [{ email: p.organizerEmail, name: p.organizerName, building: p.organizerBuilding }, ...p.members]
  return (
    <li className="card fpd__post">
      <div className="fpd__posthead">
        <div>
          <div className="nextup__eyebrow">{fmt(p.date)}{p.endMin > p.startMin ? ` · ${clock(p.startMin)} to ${clock(p.endMin)}` : ''}</div>
          <h3 className="fpd__posttitle">{p.idea.split('\n')[0]}</h3>
        </div>
        <span className="pill">{p.focus}</span>
      </div>
      {p.idea.includes('\n') && <p className="ov__p" style={{ whiteSpace: 'pre-wrap' }}>{p.idea.split('\n').slice(1).join('\n').trim()}</p>}
      <div className="work__sub">{p.place || 'Place to be decided'}</div>
      <div className="fpd__people">
        <Users size={15} aria-hidden="true" />
        {people.map((m, i) => <span key={m.email}>{m.name}{i === 0 ? ' (organizer)' : ''} · {SCOPE_LABELS[m.building]}</span>)}
        <span className="work__sub">{p.spots == null ? 'room for anyone' : full ? 'full' : `${p.spots - p.memberEmails.length} of ${p.spots} spots left`}</span>
      </div>
      <div className="fpd__postacts">
        {(organizer || member) && (
          <Link to={`/flex-pd/new?post=${p.id}`} className="btn" style={{ width: 'auto', textDecoration: 'none' }}>Start my request</Link>
        )}
        {!organizer && !member && myBuilding && p.open && !full && (
          <button className="btn" style={{ width: 'auto' }} onClick={run(() => joinPost(p, { ...me, building: myBuilding }))}>Join</button>
        )}
        {member && <button className="btn btn--ghost" style={{ width: 'auto' }} onClick={run(() => leavePost(p, me.email))}>Leave</button>}
        {organizer && (
          <>
            <button className="btn btn--ghost" style={{ width: 'auto' }} onClick={run(() => setPostOpen(p, !p.open))}>{p.open ? 'Close to new people' : 'Reopen'}</button>
            {p.memberEmails.length === 0 && <button className="linkish" onClick={run(() => deletePost(p.id))}>Withdraw</button>}
          </>
        )}
        {!p.open && <span className="pill">Closed to new people</span>}
      </div>
      {(organizer || member) && <p className="opt__hint" style={{ margin: 'var(--s-2) 0 0' }}>Joining is not approval: each of you sends your own request, and your own principal approves your part.</p>}
    </li>
  )
}

function PostForm({ onDone, myBuildings, me }: { onDone: () => void; myBuildings: Scope[]; me: { email: string; name: string } }) {
  const sy = schoolYearOf()
  const cfg = useFpdConfig()
  const days = useFpdDays(sy.id)
  const [building, setBuilding] = useState<Scope>(myBuildings[0]!)
  const [x, setX] = useState<PostInput>({ date: '', startMin: 0, endMin: 0, focus: '', place: '', idea: '', spots: null })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  if (!cfg || days === null) return <Loading label="Loading" />
  const flex = upcoming(daysFor(days.filter((d) => !d.closed), 'all'))
  const ok = x.date && x.focus && x.idea.trim() && x.date >= isoToday() && (x.endMin === 0 || x.endMin > x.startMin)
  const save = async () => {
    setBusy(true); setErr(null)
    try { await createPost(sy.id, me, building, x); onDone() } catch (e) { setErr(e instanceof Error ? e.message : String(e)) } finally { setBusy(false) }
  }
  return (
    <section className="card card--action">
      <h2 className="ov__h">Post a collaboration</h2>
      {err && <ErrorState body={`Not saved. ${err}`} />}
      {flex.length > 0 && (
        <>
          <div className="opt__label" style={{ margin: 0 }}>Flex days</div>
          <div className="chips" style={{ margin: '4px 0 var(--s-3)' }}>
            {flex.slice(0, 8).map((d) => <button key={d.id} type="button" aria-pressed={x.date === d.date} className={`chip${x.date === d.date ? ' is-on' : ''}`}
              onClick={() => setX((v) => ({ ...v, date: d.date }))}>{new Date(d.date + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}</button>)}
          </div>
        </>
      )}
      <div className="host__grid">
        <Text label="Date" type="date" value={x.date} onChange={(v) => setX((s) => ({ ...s, date: v }))} required />
        <Text label="Starts (optional)" type="time" value={x.endMin ? fromMin(x.startMin) : ''} onChange={(v) => setX((s) => ({ ...s, startMin: toMin(v) || 0 }))} />
        <Text label="Ends (optional)" type="time" value={x.endMin ? fromMin(x.endMin) : ''} onChange={(v) => setX((s) => ({ ...s, endMin: toMin(v) || 0 }))} />
      </div>
      <Area label="What you want to work on" value={x.idea} onChange={(v) => setX((s) => ({ ...s, idea: v }))} required
        placeholder={'First line is the headline, e.g.\nRewriting our 9th grade argument-writing rubric\nLooking for ELA teachers from any building.'} />
      <div className="host__grid">
        <div>
          <label className="opt__label" style={{ margin: 0, display: 'block' }}>Learning focus <span className="req">*</span></label>
          <select className="opt__input" style={{ width: '100%', marginTop: 4 }} value={x.focus} onChange={(e) => setX((s) => ({ ...s, focus: e.target.value }))}>
            <option value="">Choose…</option>
            {cfg.focus.filter((o) => !o.retired).map((o) => <option key={o.name} value={o.name}>{o.name}</option>)}
          </select>
        </div>
        <Text label="Where (optional)" value={x.place} onChange={(v) => setX((s) => ({ ...s, place: v }))} placeholder="e.g. HS library, or virtual" />
        <Text label="Room for (optional)" type="number" min={1} value={x.spots?.toString() ?? ''} placeholder="Anyone"
          onChange={(v) => setX((s) => ({ ...s, spots: v.trim() ? Math.max(1, Math.floor(Number(v))) : null }))} />
      </div>
      {myBuildings.length > 1 && (
        <div className="chips" style={{ margin: 'var(--s-3) 0 0' }}>
          {myBuildings.map((b) => <button key={b} type="button" aria-pressed={building === b} className={`chip${building === b ? ' is-on' : ''}`} onClick={() => setBuilding(b)}>{SCOPE_LABELS[b]}</button>)}
        </div>
      )}
      <div className="opt__bar">
        <button className="btn" style={{ width: 'auto' }} disabled={busy || !ok} onClick={save}>{busy ? 'Posting…' : 'Post it'}</button>
        <button className="btn btn--ghost" style={{ width: 'auto' }} onClick={onDone}>Cancel</button>
      </div>
    </section>
  )
}

/** On the Flex PD home: how many are looking, and the way in. */
export function CollaborateTeaser() {
  const { profile } = useAuth()
  const may = buildingsFor(profile?.caps, profile?.districtAdmin, 'flexpd.request').length > 0
  const posts = usePosts(schoolYearOf().id, may)
  if (!may || !posts) return null
  const open = posts.filter((p) => p.open && p.date >= isoToday()).length
  return (
    <section className="card fpd__teaser" aria-label="Find collaborators">
      <Users size={22} aria-hidden="true" />
      <div style={{ flex: 1 }}>
        <strong>{open === 0 ? 'Looking for someone to work with?' : `${open} ${open === 1 ? 'teacher is' : 'teachers are'} looking for collaborators`}</strong>
        <div className="work__sub">Post a date and an idea, or join someone in any building.</div>
      </div>
      <Link to="/flex-pd/collaborate" className="btn btn--ghost" style={{ width: 'auto', textDecoration: 'none' }}>Find collaborators</Link>
    </section>
  )
}
